//Relies on tanstack globalfilterfn
import React, { useMemo, useContext, useState, useRef, useEffect } from 'react'
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table'

import { ProductsContext } from '../context/products.context'
import FixedHeightVirtualTable from './FixedHeightVirtualTable'
import FilterPanel from './FilterPanel'
import AdvancedFilterPanel from './AdvancedFilterPanel'
import DemandHoverTooltip from './DemandHoverTooltip'
import CircularRating from './CircularRating'
import AnnualWageWithRating from './AnnualWageWithRating'
import LinksPopover from './LinksPopover'
import CipModal from './CipModal'
import BookmarkRail from './BookmarkRail'
import { PiSortAscending, PiSortDescending } from 'react-icons/pi'
import { FaSort } from 'react-icons/fa'
import { buildSearchString } from '../utils/buildSearchString';

/* ----- Helper: findCommonCipsAreas ----- */
function findCommonCipsAreas(arr1, arr2, offeredCip, showAllTotals) {
  if (!arr1 || !arr2) return false
  if (!showAllTotals) {
    return arr1.some(item => arr2.flat().includes(item) && offeredCip.includes(item))
  }
  return arr1.some(item => arr2.flat().includes(item))
}

/* ----- Custom Demand Sorting ----- */
const customDemandSorting = (rowA, rowB, columnId, desc) => {
  const a = rowA.getValue(columnId)
  const b = rowB.getValue(columnId)
  const ascendingOrder = { 1: 1, 3: 2, 2: 3 }
  const descendingOrder = { 2: 1, 3: 2, 1: 3 }
  const getRank = (value, orderMap) => {
    if (value === undefined || value === null) return Number.MAX_SAFE_INTEGER
    return orderMap[value] ?? Number.MAX_SAFE_INTEGER
  }
  const orderMap = desc ? descendingOrder : ascendingOrder
  const rankA = getRank(a, orderMap)
  const rankB = getRank(b, orderMap)
  return rankA - rankB
}

/* ----- Major Group Mapping ----- */
const majorGroupMapping = {
  "11": "Management Occupations",
  "13": "Business and Financial Operations Occupations",
  "15": "Computer and Mathematical Occupations",
  "17": "Architecture and Engineering Occupations",
  "19": "Life, Physical, and Social Science Occupations",
  "21": "Community and Social Service Occupations",
  "23": "Legal Occupations",
  "25": "Educational Instruction and Library Occupations",
  "27": "Arts, Design, Entertainment, Sports, and Media Occupations",
  "29": "Healthcare Practitioners and Technical Occupations",
  "31": "Healthcare Support Occupations",
  "33": "Protective Service Occupations",
  "35": "Food Preparation and Serving Related Occupations",
  "37": "Building and Grounds Cleaning and Maintenance Occupations",
  "39": "Personal Care and Service Occupations",
  "41": "Sales and Related Occupations",
  "43": "Office and Administrative Support Occupations",
  "45": "Farming, Fishing, and Forestry Occupations",
  "47": "Construction and Extraction Occupations",
  "49": "Installation, Maintenance, and Repair Occupations",
  "51": "Production Occupations",
  "53": "Transportation and Material Moving Occupations",
  "55": "Military Specific Occupations",
}

// Define aliases to shorten the education level names
const educationAliases = {
  "Doctoral or professional degree": "Doctoral",
  "Master's degree": "Master's",
  "Bachelor's degree": "Bachelor's",
  "Associate's degree": "Associate",
  "Postsecondary nondegree award": "Nondegree",
  "Some college, no degree": "Some college",
  "High school diploma or equivalent": "High School",
  "No formal educational credential": "None",
  "NA": "NA",
}

// Define a ranking so that a lower number = higher education
const educationRanking = {
  "Doctoral or professional degree": 1,
  "Master's degree": 2,
  "Bachelor's degree": 3,
  "Associate's degree": 4,
  "Postsecondary nondegree award": 5,
  "Some college, no degree": 6,
  "High school diploma or equivalent": 7,
  "No formal educational credential": 8,
};

// Custom sorting function that normalizes null/falsey values so they always sort to the bottom
// Custom sorting function modeled after our numeric sorts:
const educationSorting = (rowA, rowB, columnId, desc) => {
  // Normalize values: convert null/undefined to the string "undefined"
  const aVal = rowA.getValue(columnId) ?? "undefined";
  const bVal = rowB.getValue(columnId) ?? "undefined";

  // Use a helper that assigns a rank:
  const getRank = (val) => {
    // Force null, undefined, or "NA" to always get the maximum rank.
    if (val === "undefined" || val === "NA") return Number.MAX_SAFE_INTEGER;
    return educationRanking[val] ?? Number.MAX_SAFE_INTEGER;
  };

  const rankA = getRank(aVal);
  const rankB = getRank(bVal);

  // Always push rows with invalid education to the bottom.
  if (rankA === Number.MAX_SAFE_INTEGER && rankB === Number.MAX_SAFE_INTEGER) return 0;
  if (rankA === Number.MAX_SAFE_INTEGER) return 1;
  if (rankB === Number.MAX_SAFE_INTEGER) return -1;

  // For valid rows, sort by the rank.
  // When `desc` is true, we reverse the order.
  return desc ? rankB - rankA : rankA - rankB;
};

/* ----- Cell Renderers ----- */
function DemandCell({ getValue, row }) {
  if (row.original.isMajorHeader) {
    return null
  }

  const { onetSOC, onetTitle, maxDemand, education } = row.original || {}
  if (!onetSOC) return null

  return (
    <div className="relative border-zinc-200 border-r my-6 min-h-[40px] w-full py-2">
      <div>
        <span className="absolute left-4 top-1/2 -translate-y-1/2 tabular-nums text-xs text-zinc-600">
          {onetSOC.toString().replace(/\B(?=(\d{4})+(?!\d))/g, '-')}
        </span>
        <div className="cursor-pointer absolute right-1 top-1/2 -translate-y-1/2">
          <DemandHoverTooltip onetSOC={onetSOC} onetTitle={onetTitle}>
            <div className="h-[60px] -mr-2 w-[40px] place-content-center justify-items-center">
              <CircularRating demand={maxDemand} />
            </div>
          </DemandHoverTooltip>
        </div>
      </div>
    </div>
  )
}

function SalaryCell({ getValue, row }) {
  const original = row.original
  const { avgAnnualWage, avgHourlyWage, wageRating } = original
  if (!avgAnnualWage) return null
  return (
    <div className="py-2 flex items-center">
      <AnnualWageWithRating
        jobs={original}
        avgAnnualWage={avgAnnualWage}
        avgHourlyWage={avgHourlyWage}
        wageRating={wageRating}
      />
    </div>
  )
}

function GrowthCell({ row }) {
  const { longtermGrowth, growthRating } = row.original
  const growthPercent =
    longtermGrowth != null ? `${(Number(longtermGrowth) * 100).toFixed(2)}%` : null
  return (
    <div className="py-2 flex items-center">
      <div className="w-full">
        <span className="text-xs text-zinc-700">{growthPercent}</span>
        {growthRating != null && (
          <div className="h-[12px] flex mt-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className={`w-full h-3 border border-white ${
                  growthRating > i ? 'bg-sky-300' : 'bg-zinc-100'
                } ${i === 0 ? 'rounded-l' : i === 4 ? 'rounded-r' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function EmploymentCell({ row }) {
  const { estimatedEmployment } = row.original
  return (
    <div className="py-2 flex items-center">
      <div className="w-full">
        {estimatedEmployment != null ? (
          <span className="text-xs text-zinc-700">
            {Number(estimatedEmployment).toLocaleString()}
          </span>
        ) : (
          <span className="text-xs text-zinc-500"></span>
        )}
        <div className="w-full h-[12px] mt-1"></div>
      </div>
    </div>
  )
}

function OpeningsCell({ row }) {
  const { avgTotalOpenings, totalOpeningsRating } = row.original
  return (
    <div className="py-2 flex items-center">
      <div className="w-full">
        {avgTotalOpenings != null ? (
          <span className="text-xs text-zinc-700">
            {Number(avgTotalOpenings).toLocaleString()}
          </span>
        ) : (
          <span className="text-xs text-zinc-500"></span>
        )}
        {totalOpeningsRating != null && (
          <div className="h-[12px] flex mt-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className={`w-full h-3 border border-white ${
                  totalOpeningsRating > i ? 'bg-sky-300' : 'bg-zinc-100'
                } ${i === 0 ? 'rounded-l' : i === 4 ? 'rounded-r' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function OccupationTitleCell({ row, getValue }) {
  const original = row.original
  const value = getValue()
  const isMajorHeader = original?.onetSOC?.endsWith('0000')

  if (!isMajorHeader) {
    return (
      <div className="text-sm text-gray-800 font-normal flex-1">
        {value}
      </div>
    )
  }

  const socCode = original.onetSOC
  const formattedSOC = socCode.slice(0, 2) + '-' + socCode.slice(2)

  return (
    <div
      className="flex h-full items-end font-semibold text-lg rounded-t-lg -ml-[124px] min-w-[1135px] min-h-[60px] mt-[50px] pb-2 border-4 border-white bg-zinc-100 text-zinc-900"
      style={{ boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.1)' }}
    >
      <span className="mx-2 px-2 border-r border-zinc-300">{formattedSOC}</span>
      {value}
    </div>
  )
}


function EducationCell({ row, getValue }) {
  const original = row.original;
  const education = getValue();
  // Adjust this condition based on how you want to determine a “major header”
  const isMajorHeader = original?.isMajorEducation;

  if (!isMajorHeader) {
    return (
      <div className="text-sm text-gray-800 font-normal flex-1">
        {educationAliases[education] || education}
      </div>
    );
  }

  return (
    <div
      className="flex h-full items-end font-semibold text-lg rounded-t-lg -ml-[124px] min-w-[1080px] min-h-[60px] mt-[50px] pb-2 border-4 border-white bg-zinc-100 text-zinc-900"
      style={{ boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.1)' }}
    >
      <span className="mx-2 px-2 border-r border-zinc-300">
        {education}
      </span>
      {educationAliases[education] || education}
    </div>
  );
}

function MatchingCIPCell({ row }) {
  const { offeredCip } = useContext(ProductsContext)
  const value = row.original.alignedCip

  if (!value || !Array.isArray(value)) return null

  const sortedCips = [...value].sort((a, b) => {
    const aOffered = offeredCip?.includes(a)
    const bOffered = offeredCip?.includes(b)
    if (aOffered && !bOffered) return -1
    if (!aOffered && bOffered) return 1
    return 0
  })

  return (
    <div className="flex flex-wrap gap-1">
      {sortedCips.map(cip => (
        <CipModal key={cip} offeredCip={offeredCip} cipCode={cip} />
      ))}
    </div>
  )
}


function LinkCell({ row }) {
  const { userDefaults } = useContext(ProductsContext)
  const original = row.original
  const { onetSOC, linkBLS } = original
  if (!onetSOC || onetSOC.endsWith('0000')) return null
  return (
    <div className="flex items-center justify-center px-4 py-2 print:hidden">
      <LinksPopover onetSOC={onetSOC} userDefaults={userDefaults} linkBLS={linkBLS} />
    </div>
  )
}



  const columnHelper = createColumnHelper()
/**
 * Main EnhancedLaborMarketTable Component
 */
export default function EnhancedLaborMarketTable() {
  // Get advancedFilters from context instead of using local state.
  const { jobs, workforceMenu, offeredCip, advancedFilters, setAdvancedFilters, init } = useContext(ProductsContext);
  const [selectedFilterLMI, setSelectedFilterLMI] = useState({ label: '(All)', value: '(All)' })
  const [showAllTotals, setShowAllTotals] = useState(true)


  const salaryLookup = useMemo(() => {
    if (!jobs?.socData) return [0, 0, 0, 0, 0];
    const groups = {};
    jobs.socData.forEach(row => {
      const rating = row.wageRating;
      if (rating != null && row.avgAnnualWage != null) {
        groups[rating] = groups[rating] || [];
        groups[rating].push(row.avgAnnualWage);
      }
    });
    const lookup = [];
    for (let r = 1; r <= 5; r++) {
      if (groups[r]?.length) {
        // For ratings 1–4, use the floor of the minimum value;
        // for rating 5, use the ceiling of the maximum value.
        lookup.push(r < 5 
          ? Math.floor(Math.min(...groups[r])) 
          : Math.ceil(Math.max(...groups[r])));
      } else {
        lookup.push(0);
      }
    }
    return lookup;
  }, [jobs]);
  
  const growthLookup = useMemo(() => {
    if (!jobs?.socData) return [0, 0, 0, 0, 0];
    const groups = {};
    jobs.socData.forEach(row => {
      const rating = row.growthRating; // group by growthRating
      if (rating != null && row.longtermGrowth != null) {
        groups[rating] = groups[rating] || [];
        groups[rating].push(row.longtermGrowth);
      }
    });
    const lookup = [];
    for (let r = 1; r <= 5; r++) {
      if (groups[r]?.length) {
        // For growth, you might allow decimals. Adjust rounding if desired.
        lookup.push(r < 5 
          ? Math.min(...groups[r]) 
          : Math.max(...groups[r]));
      } else {
        lookup.push(0);
      }
    }
    return lookup;
  }, [jobs]);
  
  const openingsLookup = useMemo(() => {
    if (!jobs?.socData) return [0, 0, 0, 0, 0];
    const groups = {};
    jobs.socData.forEach(row => {
      const rating = row.totalOpeningsRating; // group by totalOpeningsRating
      if (rating != null && row.avgTotalOpenings != null) {
        groups[rating] = groups[rating] || [];
        groups[rating].push(row.avgTotalOpenings);
      }
    });
    const lookup = [];
    for (let r = 1; r <= 5; r++) {
      if (groups[r]?.length) {
        lookup.push(r < 5 
          ? Math.floor(Math.min(...groups[r])) 
          : Math.ceil(Math.max(...groups[r])));
      } else {
        lookup.push(0);
      }
    }
    return lookup;
  }, [jobs]);

const columns = useMemo(
  () => [
    columnHelper.accessor(row => row.maxDemand ?? undefined, {
      id: 'maxDemand',
      header: 'Demand',
      cell: DemandCell,
      sortingFn: customDemandSorting,
      sortUndefined: 'last',
      sortDescFirst: false,
      size: 120,
    }),
    columnHelper.accessor(row => row.onetTitle, {
      id: 'onetTitle',
      header: 'Occupation Title',
      cell: OccupationTitleCell,
      size: 150,
      maxSize: 220,
      meta: { responsive: true },
    }),
    columnHelper.accessor(row => {
      // If row.education is null or 'NA', treat it as undefined
      // so `sortUndefined: 'last'` pushes it down.
      if (row.education == null || row.education === 'null' || row.education === 'NA') {
        return undefined
      }
      return row.education
    }, {
      id: 'requiredEducation',
      header: 'Typical Education for Entry',
      cell: EducationCell,
      sortingFn: educationSorting,   // still use your custom ranking
      sortUndefined: 'last',         // ensure undefined sorts last
      sortDescFirst: true,
      size: 100,
    }),
    columnHelper.accessor(row => row.avgAnnualWage ?? undefined, {
      id: 'avgAnnualWage',
      header: 'Median Annual Salary',
      cell: SalaryCell,
      sortingFn: 'basic',
      sortUndefined: 'last',
      sortDescFirst: true,
      size: 100,
    }),
    columnHelper.accessor(row => row.avgTotalOpenings ?? undefined, {
      id: 'avgTotalOpenings',
      header: 'Annual Openings',
      cell: OpeningsCell,
      sortingFn: 'basic',
      sortUndefined: 'last',
      size: 100,
    }),
   
    columnHelper.accessor(row => row.longtermGrowth ?? undefined, {
      id: 'longtermGrowth',
      header: 'Growth',
      cell: GrowthCell,
      sortingFn: 'basic',
      sortUndefined: 'last',
      size: 90,
    }),
    columnHelper.accessor(row => row.estimatedEmployment ?? undefined, {
      id: 'estimatedEmployment',
      header: 'Employment',
      cell: EmploymentCell,
      sortingFn: 'basic',
      sortUndefined: 'last',
      size: 115,
    }),
    columnHelper.accessor(row => row.alignedCip, {
      id: 'alignedCip',
      header: 'Matching CIP',
      cell: MatchingCIPCell,
      enableSorting: false,
      size: 240,
    }),
    columnHelper.display({
      id: 'links',
      header: 'Links',
      cell: LinkCell,
      enableSorting: false,
      size: 60,
    }),
  ],
  []
)
  

  // Sorting and global filter state
  const [sorting, setSorting] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')
  // 1. Define your global filter function
  const globalSearchFn = (row, columnId, filterValue) => {
    // Build one big search string from row.original
    // Pass the CIP menu so it can convert CIP codes to CIP names.
    const searchStr = buildSearchString(row.original, init.cipMenu);

    // Check if the user’s typed filterValue is included
    // in that big search string (case-insensitive).
    return searchStr.includes(filterValue.toLowerCase());
  };
  

  // Compute the filtered rows (before grouping, etc.)
const filteredDetailRows = useMemo(() => {
  if (!jobs?.socData) return [];
  const detailRows = jobs.socData.filter(
    row => row?.onetSOC && !row.onetSOC.endsWith('0000')
  );
  if (selectedFilterLMI.value !== '(All)' && workforceMenu) {
    const arrOfCips = workforceMenu.assignProgramClusterCips
      .map(e => e[selectedFilterLMI.value])
      .filter(Boolean);
    return detailRows.filter(row => {
      if (!row?.alignedCip) return false;
      return findCommonCipsAreas(row.alignedCip, arrOfCips, offeredCip, showAllTotals);
    });
  }
  return detailRows;
}, [jobs, selectedFilterLMI, workforceMenu, offeredCip, showAllTotals]);

// Now calculate actual ranges based on the filtered rows:
const salaryLimits = useMemo(() => {
  if (!filteredDetailRows.length) return { min: 0, max: 999999 };
  return {
    min: Math.min(...filteredDetailRows.map(row => row.avgAnnualWage || 0)),
    max: Math.max(...filteredDetailRows.map(row => row.avgAnnualWage || 0)),
  };
}, [filteredDetailRows]);

const openingsLimits = useMemo(() => {
  if (!filteredDetailRows.length) return { min: 0, max: 999999 };
  return {
    min: Math.min(...filteredDetailRows.map(row => row.avgTotalOpenings || 0)),
    max: Math.max(...filteredDetailRows.map(row => row.avgTotalOpenings || 0)),
  };
}, [filteredDetailRows]);

const growthLimits = useMemo(() => {
  if (!filteredDetailRows.length) return { min: 0, max: 1 };
  return {
    min: Math.min(...filteredDetailRows.map(row => row.longtermGrowth || 0)),
    max: Math.max(...filteredDetailRows.map(row => row.longtermGrowth || 0)),
  };
}, [filteredDetailRows]);



  // Data transformation: group detail rows by major and inject header rows.
  const data = useMemo(() => {
    if (!jobs?.socData) return []
    const detailRows = jobs.socData.filter(
      row => row?.onetSOC && !row.onetSOC.endsWith('0000')
    )

    let filteredDetailRows = detailRows
    if (selectedFilterLMI.value !== '(All)' && workforceMenu) {
      const arrOfCips = workforceMenu.assignProgramClusterCips
        .map(e => e[selectedFilterLMI.value])
        .filter(Boolean)
      filteredDetailRows = detailRows.filter(row => {
        if (!row?.alignedCip) return false
        return findCommonCipsAreas(row.alignedCip, arrOfCips, offeredCip, showAllTotals)
      })
    }

    const groups = {}
    filteredDetailRows.forEach(row => {
      const majorKey = row.onetSOC.replace(/\D/g, '').slice(0, 2)
      const majorCode = majorKey + '0000'
      if (!groups[majorCode]) {
        groups[majorCode] = []
      }
      groups[majorCode].push(row)
    })

    const groupOrder = []
    for (const row of jobs.socData) {
      if (row?.onetSOC && !row.onetSOC.endsWith('0000')) {
        const majorKey = row.onetSOC.replace(/\D/g, '').slice(0, 2)
        const majorCode = majorKey + '0000'
        if (groups[majorCode] && !groupOrder.includes(majorCode)) {
          groupOrder.push(majorCode)
        }
      }
    }

    const finalData = []
    for (const majorCode of groupOrder) {
      const majorKey = majorCode.slice(0, 2)
      const headerRow = {
        onetSOC: majorCode,
        majorGroup: majorKey,
        onetTitle: majorGroupMapping[majorKey] || majorCode,
        isMajorHeader: true,
      }
      finalData.push(headerRow)
      finalData.push(...groups[majorCode])
    }

    return finalData
  }, [jobs, workforceMenu, selectedFilterLMI, offeredCip, showAllTotals])

  // ---- Create a custom function to apply advanced filters ----
  const applyAdvancedFilters = (row) => {
    const occupation = row.original
    if (!occupation) return false

    // 1) Filter by education
    if (advancedFilters.education.length > 0) {
      if (!advancedFilters.education.includes(occupation.education)) {
        return false
      }
    }

    // 2) Salary range
    const annualWage = occupation.avgAnnualWage ?? 0
    if (annualWage < advancedFilters.salaryRange[0] || annualWage > advancedFilters.salaryRange[1]) {
      return false
    }

    // 3) Number of openings range
    const openings = occupation.avgTotalOpenings ?? 0
    if (openings < advancedFilters.openingsRange[0] || openings > advancedFilters.openingsRange[1]) {
      return false
    }

    // 4) Growth range (0 to 1 => 0% to 100%)
    const growth = occupation.longtermGrowth ?? 0
    if (growth < advancedFilters.growthRange[0] || growth > advancedFilters.growthRange[1]) {
      return false
    }

    // 5) CIP offered?
    if (advancedFilters.hasCipOffered) {
      const hasOffered = occupation.alignedCip?.some(c => offeredCip?.includes(c))
      if (!hasOffered) return false
    }

    // 6) Is STEM?
    if (advancedFilters.isStem) {
      if (!occupation.isSTEM) return false
    }

    return true
  }

  // ---- Filter the data via advancedFilters ----
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return []
    return data.filter((row) => {
      // Keep header rows (or optionally check if at least one detail row follows)
      if (row?.isMajorHeader) {
        return true
      }
      return applyAdvancedFilters({ original: row })
    })
  }, [data, advancedFilters])

  // ---- Create the TanStack table instance using filteredData ----
  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      globalFilter,
    },
 // Here’s the key part:
    //   - custom globalFilterFn references your buildSearchString
    //   - onGlobalFilterChange sets `globalFilter` from your search field
    globalFilterFn: globalSearchFn,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableMultiSort: true,
  })

  const preGlobalFilteredRows = table.getPrePaginationRowModel().rows
  const allRows = table.getRowModel().rows
  const sortingActive = sorting.length > 0
  const displayRows = useMemo(() => {
    if (!sortingActive) return allRows
    return allRows.filter(r => !r.original?.isMajorHeader)
  }, [allRows, sortingActive])

  // Build bookmarks for the sidebar (only when not sorted)
  const bookmarks = useMemo(() => {
    if (sortingActive) return {}
    const bm = {}
    allRows.forEach((row, idx) => {
      const original = row.original
      if (original?.onetSOC) {
        const majorKey = original.majorGroup || original.onetSOC.replace(/\D/g, '').slice(0, 2)
        const major = majorKey + '0000'
        if (bm[major] === undefined) {
          bm[major] = idx
        }
      }
    })
    return bm
  }, [allRows, sortingActive])

  const bookmarkArray = useMemo(() => {
    return Object.entries(bookmarks)
      .map(([major, index]) => ({
        major,
        index,
        label: majorGroupMapping[major.slice(0, 2)] || major,
      }))
      .sort((a, b) => a.index - b.index)
  }, [bookmarks])

  // ---- Virtualizer setup ----
  const virtualizerRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const [activeMajor, setActiveMajor] = useState(null)

  function handleVirtualizerCreated(rv) {
    if (!rv) return
    virtualizerRef.current = rv
    scrollContainerRef.current = rv.getScrollElement
      ? rv.getScrollElement()
      : rv.scrollElement
    if (scrollContainerRef.current) {
      scrollContainerRef.current.addEventListener('scroll', handleScroll)
    }
  }

  useEffect(() => {
    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  function handleScroll() {
    if (!virtualizerRef.current) return
    const virtualItems = virtualizerRef.current.getVirtualItems()
    if (!virtualItems?.length) return
    const scroller = scrollContainerRef.current
    const scrollTop = scroller?.scrollTop ?? 0
    let found = null
    for (const item of virtualItems) {
      const itemStart = item.start
      const itemEnd = item.start + item.size
      if (itemStart >= scrollTop && itemStart <= scrollTop + 20) {
        found = item
        break
      }
      if (itemStart < scrollTop && itemEnd > scrollTop) {
        found = item
        break
      }
    }
    if (!found) {
      found = virtualItems[0]
    }
    if (!found) return
    const rowIndex = found.index
    const row = allRows[rowIndex]
    if (row?.original?.onetSOC) {
      const majorKey = row.original.majorGroup || row.original.onetSOC.replace(/\D/g, '').slice(0, 2)
      const major = majorKey + '0000'
      setActiveMajor(major)
    }
  }

  // ---- Scroll to top when sorting changes ----
  useEffect(() => {
    jumpToRow(0)
  }, [sorting])

  function jumpToRow(rowIndex) {
    if (!virtualizerRef.current) return
    virtualizerRef.current.scrollToIndex(rowIndex, { align: 'start' })
  }

  return (
    <div className="w-full flex justify-center">
      <div className="flex w-full max-w-[1300px]">
        <BookmarkRail
          bookmarks={bookmarkArray}
          activeMajor={activeMajor}
          onJump={jumpToRow}
          hidden={sortingActive}
        />
        <div className="space-y-4 flex-grow max-w-[1170px]">
        <FilterPanel
  preGlobalFilteredRows={table.getPrePaginationRowModel().rows}
  globalFilter={globalFilter}
  setGlobalFilter={setGlobalFilter}
  selectedFilterLMI={selectedFilterLMI}
  setSelectedFilterLMI={setSelectedFilterLMI}
/>

          <AdvancedFilterPanel
         advancedFilters={advancedFilters}
         setAdvancedFilters={setAdvancedFilters}
         salaryLimits={salaryLimits}
         openingsLimits={openingsLimits}
         growthLimits={growthLimits}
         salaryLookup={salaryLookup}
         growthLookup={growthLookup}
         openingsLookup={openingsLookup}
          />
          <div className="border border-gray-300 bg-[#E3E3E3] rounded shadow-sm overflow-auto">
            {/* Table Header */}
            <div className="cursor-pointer select-none bg-zinc-50 border-b border-gray-300 overflow-x-auto">
              {table.getHeaderGroups().map(headerGroup => (
                <div key={headerGroup.id} className="flex w-max min-w-full">
                  {headerGroup.headers.map(header => {
                    const toggleSortProps = {
                      onClick: header.column.getToggleSortingHandler(),
                    }
                    const isSorted = !!header.column.getIsSorted()
                    const isDesc = header.column.getIsSorted() === 'desc'
                    return (
                      <div
                        key={header.id}
                        {...toggleSortProps}
                        style={{
                          minWidth: header.column.id === 'onetTitle'
                            ? header.column.columnDef.minSize
                            : header.column.columnDef.size,
                          width: header.column.id === 'onetTitle'
                            ? 'auto'
                            : header.column.columnDef.size,
                          flexGrow: header.column.id === 'onetTitle' ? 1 : 0,
                          maxWidth: header.column.id === 'onetTitle'
                            ? header.column.columnDef.maxSize
                            : header.column.columnDef.size,
                        }}
                        className={`flex items-center justify-start px-2 py-2 text-sm font-semibold border-r border-gray-200 ${
                          isSorted ? 'bg-amber-50 text-orange-500' : 'text-gray-700'
                        }`}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          isSorted ? (
                            isDesc ? (
                              <PiSortDescending className="w-4 h-4 inline-block ml-1 flex-shrink-0" />
                            ) : (
                              <PiSortAscending className="w-4 h-4 inline-block ml-1 flex-shrink-0" />
                            )
                          ) : (
                            <FaSort className="w-4 h-4 inline-block ml-1 text-gray-400 flex-shrink-0" />
                          )
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
            {/* Virtualized Table Body */}
            <FixedHeightVirtualTable
              rows={displayRows}
              prepareRow={row => row.getVisibleCells()}
              containerHeight={600}
              onVirtualizerCreated={handleVirtualizerCreated}
              activeMajor={activeMajor}
            />
          </div>
        </div>
      </div>
    </div>
  )
}